import { useParams, useNavigate } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { communities } from '@/mocks/communities';
import ScrollBatteryBar from '@/components/feature/ScrollBatteryBar';
import SiteHeader from '@/components/feature/SiteHeader';
import CommunityCard from '@/pages/home/components/CommunityCard';
import Footer from '@/pages/home/components/Footer';

const categoryMeta: Record<string, { icon: string; color: string; desc: string }> = {
  IT: {
    icon: 'ri-code-s-slash-line',
    color: '#0088CC',
    desc: 'Explore communities for developers, engineers, and technology enthusiasts across AI, Web3, mobile, and more.',
  },
  Finance: {
    icon: 'ri-line-chart-line',
    color: '#16a34a',
    desc: 'Crypto traders, forex experts, startup founders, and investors sharing insights and strategies.',
  },
  Games: {
    icon: 'ri-gamepad-line',
    color: '#7c3aed',
    desc: 'Game developers, gamers, and esports communities sharing projects, tips, and entertainment.',
  },
  Health: {
    icon: 'ri-heart-pulse-line',
    color: '#e11d48',
    desc: 'Healthcare professionals, fitness enthusiasts, and wellness advocates connecting for better living.',
  },
  Arts: {
    icon: 'ri-palette-line',
    color: '#d97706',
    desc: 'Artists, designers, photographers, and creatives inspiring each other and showcasing their work.',
  },
  Education: {
    icon: 'ri-book-open-line',
    color: '#0891b2',
    desc: 'Students, educators, and lifelong learners sharing knowledge, resources, and study groups.',
  },
  Business: {
    icon: 'ri-briefcase-line',
    color: '#64748b',
    desc: 'Entrepreneurs, consultants, and business professionals networking and growing together.',
  },
};

const categoryDisplayNames: Record<string, string> = {
  IT: 'IT & Technology',
  Finance: 'Finance & Crypto',
  Games: 'Games & Entertainment',
  Health: 'Health & Wellness',
  Arts: 'Arts & Creative',
  Education: 'Education',
  Business: 'Business',
};

const allCategoryKeys = Object.keys(categoryDisplayNames);

export default function CategoryPage() {
  const { category } = useParams<{ category: string }>();
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState<'members' | 'activity' | 'newest'>('members');
  const [activeTag, setActiveTag] = useState<string | null>(null);

  // All tags in this category with counts
  const categoryTags = useMemo(() => {
    if (!category) return [];
    const tagMap: Record<string, number> = {};
    communities
      .filter((c) => c.category.toLowerCase() === category.toLowerCase())
      .forEach((c) => {
        c.tags.forEach((t) => {
          tagMap[t] = (tagMap[t] || 0) + 1;
        });
      });
    return Object.entries(tagMap)
      .sort((a, b) => b[1] - a[1])
      .map(([tag, count]) => ({ tag, count }));
  }, [category]);

  const matched = useMemo(() => {
    if (!category) return [];
    let list = communities.filter(
      (c) => c.category.toLowerCase() === category.toLowerCase()
    );
    if (activeTag) {
      list = list.filter((c) =>
        c.tags.some((t) => t.toLowerCase() === activeTag.toLowerCase())
      );
    }
    if (sortBy === 'members') list = [...list].sort((a, b) => b.memberCount - a.memberCount);
    else if (sortBy === 'newest')
      list = [...list].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    else if (sortBy === 'activity') {
      const order = { high: 0, medium: 1, low: 2 };
      list = [...list].sort((a, b) => order[a.activityLevel] - order[b.activityLevel]);
    }
    return list;
  }, [category, sortBy, activeTag]);

  const meta = category ? categoryMeta[category] ?? categoryMeta['IT'] : categoryMeta['IT'];
  const displayName = category ? categoryDisplayNames[category] ?? category : category;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f5f8fb] via-[#f8fafc] to-[#f3f7fa] text-slate-800 font-sans">
      <ScrollBatteryBar />
      <SiteHeader showBack />

      <main className="max-w-screen-2xl mx-auto px-4 md:px-6 py-6">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs text-slate-400 mb-5">
          <button
            onClick={() => navigate('/')}
            className="hover:text-slate-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            Home
          </button>
          <span>/</span>
          <span className="text-slate-500">Directory</span>
          <span>/</span>
          <span className="font-medium" style={{ color: meta.color }}>
            {displayName}
          </span>
          {activeTag && (
            <>
              <span>/</span>
              <span className="text-[#0088CC] font-medium">#{activeTag}</span>
            </>
          )}
        </nav>

        <div className="flex gap-6 items-start">
          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Page Header */}
            <div className="bg-white/50 backdrop-blur-md rounded-2xl border border-white/80 px-6 py-5 mb-5">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <span
                      className="w-10 h-10 flex items-center justify-center rounded-xl border shrink-0"
                      style={{ background: `${meta.color}15`, borderColor: `${meta.color}30` }}
                    >
                      <i className={`${meta.icon} text-lg`} style={{ color: meta.color }}></i>
                    </span>
                    <div>
                      <h1 className="text-2xl font-extrabold text-slate-800">
                        {displayName}
                        {activeTag && (
                          <span className="ml-2 text-base font-semibold text-[#0088CC]">/ #{activeTag}</span>
                        )}
                      </h1>
                      <p className="text-xs text-slate-400">
                        {matched.length} communit{matched.length !== 1 ? 'ies' : 'y'} found
                        {activeTag && (
                          <button
                            onClick={() => setActiveTag(null)}
                            className="ml-2 text-[#0088CC] hover:underline cursor-pointer"
                          >
                            Clear tag filter
                          </button>
                        )}
                      </p>
                    </div>
                  </div>
                  {!activeTag && (
                    <p className="text-sm text-slate-500 hidden sm:block" style={{ paddingLeft: '3.25rem' }}>
                      {meta.desc}
                    </p>
                  )}
                </div>

                {/* Sort */}
                <div className="flex items-center gap-2 flex-wrap shrink-0">
                  <span className="text-xs text-slate-400 whitespace-nowrap">Sort by:</span>
                  {(
                    [
                      { key: 'members', label: 'Most Members' },
                      { key: 'activity', label: 'Most Active' },
                      { key: 'newest', label: 'Newest' },
                    ] as const
                  ).map((opt) => (
                    <button
                      key={opt.key}
                      onClick={() => setSortBy(opt.key)}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-all cursor-pointer whitespace-nowrap backdrop-blur-sm ${
                        sortBy === opt.key
                          ? 'bg-[#0088CC]/80 border-[#0088CC]/60 text-white'
                          : 'bg-white/50 border-[#b8d4ea]/60 text-slate-500 hover:border-[#0088CC]/30 hover:text-slate-700'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Mobile tag chips */}
              {categoryTags.length > 0 && (
                <div className="xl:hidden mt-4 pt-4 border-t border-[#c5d9eb]/30">
                  <p className="text-xs text-slate-400 mb-2 font-medium">Filter by tag:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {activeTag && (
                      <button
                        onClick={() => setActiveTag(null)}
                        className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border bg-[#0088CC]/10 border-[#0088CC]/30 text-[#0088CC] cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-close-line text-xs"></i>
                        All
                      </button>
                    )}
                    {categoryTags.map(({ tag, count }) => (
                      <button
                        key={tag}
                        onClick={() => setActiveTag(activeTag === tag ? null : tag)}
                        className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border transition-all cursor-pointer whitespace-nowrap ${
                          activeTag === tag
                            ? 'bg-[#0088CC] border-[#0088CC] text-white'
                            : 'bg-white/60 border-[#c5d9eb]/50 text-slate-500 hover:border-[#0088CC]/30 hover:text-[#0088CC]'
                        }`}
                      >
                        #{tag}
                        <span className={`text-[10px] font-semibold ${activeTag === tag ? 'text-white/70' : 'text-slate-400'}`}>
                          {count}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Results */}
            {matched.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5">
                {matched.map((c) => (
                  <CommunityCard key={c.id} community={c} />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-24 text-center bg-white/50 backdrop-blur-md rounded-2xl border border-white/80">
                <div
                  className="w-14 h-14 flex items-center justify-center rounded-full mb-4"
                  style={{ background: `${meta.color}15` }}
                >
                  <i className={`${meta.icon} text-2xl`} style={{ color: meta.color }}></i>
                </div>
                <h3 className="text-lg font-semibold text-slate-700 mb-2">No communities found</h3>
                <p className="text-slate-400 text-sm mb-5">
                  {activeTag
                    ? <>No communities tagged <strong>#{activeTag}</strong> in {displayName}.</>
                    : <>No communities in <strong>{displayName}</strong> yet.</>
                  }
                </p>
                <div className="flex gap-3">
                  {activeTag && (
                    <button
                      onClick={() => setActiveTag(null)}
                      className="flex items-center gap-2 bg-white/70 hover:bg-white border border-[#b8d4ea]/60 text-slate-600 text-sm font-semibold px-5 py-2.5 rounded-xl transition-all cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-close-line w-4 h-4 flex items-center justify-center"></i>
                      Clear Tag
                    </button>
                  )}
                  <button
                    onClick={() => navigate('/')}
                    className="flex items-center gap-2 bg-[#0088CC] hover:bg-[#0099DD] text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
                    Browse All
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Right Sidebar */}
          <aside className="hidden xl:flex flex-col w-64 shrink-0 sticky top-20 gap-4">
            {/* Tags panel */}
            {categoryTags.length > 0 && (
              <div className="bg-[#ddeaf5]/75 backdrop-blur-xl rounded-2xl border border-[#b8d4ea]/60 p-4">
                <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                  <span className="w-4 h-4 flex items-center justify-center text-[#0088CC]">
                    <i className="ri-price-tag-3-line text-sm"></i>
                  </span>
                  Filter by Tag
                  {activeTag && (
                    <button
                      onClick={() => setActiveTag(null)}
                      className="ml-auto text-[10px] text-[#0088CC] hover:underline cursor-pointer whitespace-nowrap"
                    >
                      Clear
                    </button>
                  )}
                </h3>
                <div className="flex flex-wrap gap-1.5 max-h-52 overflow-y-auto [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: 'none' }}>
                  {categoryTags.map(({ tag, count }) => (
                    <button
                      key={tag}
                      onClick={() => setActiveTag(activeTag === tag ? null : tag)}
                      className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border transition-all cursor-pointer whitespace-nowrap ${
                        activeTag === tag
                          ? 'bg-[#0088CC] border-[#0088CC] text-white'
                          : 'bg-white/60 border-[#c5d9eb]/50 text-slate-500 hover:border-[#0088CC]/30 hover:text-[#0088CC]'
                      }`}
                    >
                      #{tag}
                      <span className={`text-[10px] font-semibold ${activeTag === tag ? 'text-white/70' : 'text-slate-400'}`}>
                        {count}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* All Categories panel */}
            <div className="bg-[#ddeaf5]/75 backdrop-blur-xl rounded-2xl border border-[#b8d4ea]/60 p-4">
              <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                <span className="w-4 h-4 flex items-center justify-center text-[#0088CC]">
                  <i className="ri-apps-line text-sm"></i>
                </span>
                All Categories
              </h3>
              <div className="flex flex-col gap-1">
                {allCategoryKeys.map((key) => {
                  const m = categoryMeta[key];
                  const isActive = key.toLowerCase() === (category ?? '').toLowerCase();
                  return (
                    <button
                      key={key}
                      onClick={() => { navigate(`/category/${key}`); setActiveTag(null); }}
                      className={`flex items-center gap-2.5 px-3 py-2 rounded-xl border transition-all cursor-pointer text-left ${
                        isActive
                          ? 'border-transparent text-white'
                          : 'bg-white/50 border-[#c5d9eb]/50 text-slate-600 hover:border-[#0088CC]/30 hover:text-slate-800 hover:bg-white/70'
                      }`}
                      style={isActive ? { background: m.color, borderColor: m.color } : {}}
                    >
                      <span
                        className="w-6 h-6 flex items-center justify-center rounded-lg shrink-0"
                        style={isActive ? { background: 'rgba(255,255,255,0.25)' } : { background: `${m.color}15` }}
                      >
                        <i className={`${m.icon} text-xs`} style={{ color: isActive ? '#fff' : m.color }}></i>
                      </span>
                      <span className="text-xs font-medium flex-1 whitespace-nowrap overflow-hidden text-ellipsis">
                        {categoryDisplayNames[key]}
                      </span>
                      <span className={`text-[10px] font-semibold ${isActive ? 'text-white/70' : 'text-slate-400'}`}>
                        {communities.filter((c) => c.category.toLowerCase() === key.toLowerCase()).length}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
}
